import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { EmptyState } from './EmptyState';
import { MasterclassCard } from './MasterclassCard';
import { useMasterclassHome } from '../hooks/useMasterclassHome';

export const MasterclassHomeScreen = () => {
  const { masterclasses, loading, error, hasMore, loadMore } = useMasterclassHome();

  // Loading state - show skeleton cards
  if (loading && masterclasses.length === 0) {
    return (
      <div className="flex h-full flex-col bg-background">
        <div className="flex-1 overflow-auto p-8">
          <div className="mx-auto max-w-4xl space-y-8">
            <div>
              <Skeleton className="h-9 w-64 mb-2" />
              <Skeleton className="h-5 w-96" />
            </div>
            <div className="grid gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full flex-col bg-background">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="flex flex-col items-center text-center">
            <p className="text-destructive text-lg font-medium mb-2">
              Failed to load masterclasses
            </p>
            <p className="text-muted-foreground mb-4">
              {error.message || 'An unexpected error occurred'}
            </p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (masterclasses.length === 0) {
    return (
      <div className="flex h-full flex-col bg-background">
        <div className="flex-1 overflow-auto p-8">
          <div className="mx-auto max-w-4xl">
            <EmptyState />
          </div>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex-1 overflow-auto p-8">
        <div className="mx-auto max-w-4xl space-y-8">
          {/* Header */}
          <div>
            <h1 className="mb-2 text-3xl font-bold text-card-foreground">Your Masterclasses</h1>
            <p className="text-muted-foreground">Select a masterclass to view its patient cases</p>
          </div>

          {/* Masterclass Grid */}
          <div className="grid gap-4">
            {masterclasses.map((mc) => (
              <MasterclassCard key={mc.id} masterclass={mc} />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
